import React, { useState } from 'react';
import { X, BookOpen, Loader2, AlertCircle, Plus, Trash2, Save } from 'lucide-react';
import { updateModule, createElement, updateElement as updateElementApi, deleteElement, updateElementWeights } from '@/services/filieresService';
import type { ModuleWithElements, UpdateModuleData, ElementWeightUpdate } from '@/types/filiere.types';
import DeleteImpactModal from './filieres/DeleteImpactModal';

interface ElementEdit {
  id: string;
  name: string;
  weight: number;
  volumeHoraire: number | null;
  isNew?: boolean;
}

interface EditModuleModalProps {
  module: ModuleWithElements;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
  onUpdate: () => void;
}

const EditModuleModal: React.FC<EditModuleModalProps> = ({
  module,
  onClose,
  onSuccess,
  onError,
  onUpdate,
}) => {
  const [formData, setFormData] = useState<UpdateModuleData>({
    name: module.name,
    code: module.code,
    coefficient: module.coefficient,
  });
  const [elements, setElements] = useState<ElementEdit[]>(
    module.elements.map(el => ({
      id: el.id,
      name: el.name,
      weight: el.weight,
      volumeHoraire: el.volumeHoraire || null,
    }))
  );
  const [deletedElementIds, setDeletedElementIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Removed elements are wiped with their grades: confirm the impact before saving
  const [showDeleteImpact, setShowDeleteImpact] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'coefficient' ? parseFloat(value) || 1 : value,
    }));
  };

  // Generate unique ID for new elements
  const generateId = () => `new-${Math.random().toString(36).substr(2, 9)}`;

  // Add element
  const addElement = () => {
    const newElements = [...elements, { id: generateId(), name: '', weight: 0, volumeHoraire: null, isNew: true }];
    // Redistribute weights
    const weightPerElement = Math.floor(100 / newElements.length);
    const remainder = 100 - weightPerElement * newElements.length;
    setElements(newElements.map((el, idx) => ({
      ...el,
      weight: weightPerElement + (idx === 0 ? remainder : 0),
    })));
  };

  // Remove element
  const removeElement = (elementId: string) => {
    if (elements.length <= 1) return;

    const element = elements.find(el => el.id === elementId);
    if (element && !element.isNew) {
      setDeletedElementIds(prev => [...prev, elementId]);
    }

    const newElements = elements.filter(el => el.id !== elementId);
    // Redistribute weights
    const weightPerElement = Math.floor(100 / newElements.length);
    const remainder = 100 - weightPerElement * newElements.length;
    setElements(newElements.map((el, idx) => ({
      ...el,
      weight: weightPerElement + (idx === 0 ? remainder : 0),
    })));
  };

  // Update element
  const updateElement = (elementId: string, field: keyof ElementEdit, value: string | number | null) => {
    setElements(prev => prev.map(el =>
      el.id === elementId ? { ...el, [field]: value } : el
    ));
  };

  // Calculate total weight
  const getTotalWeight = () => {
    return elements.filter(el => el.name.trim()).reduce((sum, el) => sum + el.weight, 0);
  };

  // Validate form
  const validateForm = (): string | null => {
    if (!formData.code?.trim()) return 'Le code du module est requis';
    if (!formData.name?.trim()) return 'Le nom du module est requis';
    if ((formData.coefficient || 0) < 0.1) return 'Le coefficient doit être >= 0.1';

    const validElements = elements.filter(el => el.name.trim());
    if (validElements.length === 0) return 'Au moins un élément est requis';

    const totalWeight = validElements.reduce((sum, el) => sum + el.weight, 0);
    if (Math.abs(totalWeight - 100) > 0.1) {
      return `Les poids des éléments doivent totaliser 100% (actuellement: ${totalWeight}%)`;
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (deletedElementIds.length > 0) {
      setShowDeleteImpact(true);
      return;
    }

    await performSave();
  };

  const performSave = async (): Promise<void> => {
    setShowDeleteImpact(false);
    setIsSubmitting(true);

    try {
      // 1. Update module basic info
      await updateModule(module.id, formData);

      // 2. Delete removed elements
      for (const elementId of deletedElementIds) {
        await deleteElement(elementId);
      }

      // 3. Create new elements (with volumeHoraire), keeping their ids for the weights step.
      //    Existing elements whose name changed are renamed here: the weights endpoint
      //    only carries weight + volumeHoraire, so a rename would otherwise be dropped.
      const originalNames = new Map(module.elements.map(el => [el.id, el.name]));
      const validElements = elements.filter(el => el.name.trim());
      const weights: ElementWeightUpdate[] = [];
      for (const element of validElements) {
        const name = element.name.trim();
        if (element.isNew) {
          const created = await createElement({
            moduleId: module.id,
            name,
            volumeHoraire: element.volumeHoraire,
          });
          weights.push({ elementId: created.id, weight: element.weight, volumeHoraire: element.volumeHoraire });
        } else {
          if (originalNames.get(element.id) !== name) {
            await updateElementApi(element.id, { name });
          }
          weights.push({ elementId: element.id, weight: element.weight, volumeHoraire: element.volumeHoraire });
        }
      }

      // 4. Update weights and volumeHoraire for ALL elements (the backend requires the full set)
      if (weights.length > 0) {
        await updateElementWeights(module.id, { weights });
      }

      // Store scroll position before update
      const scrollContainer = document.querySelector('.overflow-y-auto');
      const scrollTop = scrollContainer?.scrollTop || 0;

      onSuccess('Module mis à jour avec succès');
      onUpdate();
      onClose();

      // Restore scroll position after update (with a small delay for React to re-render)
      requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollTop;
        }
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
      setError(message);
      onError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalWeight = getTotalWeight();
  const isWeightValid = Math.abs(totalWeight - 100) <= 0.1;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Modifier le Module</h2>
                <p className="text-sm text-gray-500">{module.code} - {module.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Module Info */}
              <div className="grid grid-cols-3 gap-4">
                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code || ''}
                    onChange={handleChange}
                    placeholder="POO101"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>

                {/* Nom */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du Module <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    placeholder="Programmation Orientée Objet"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>
              </div>

              {/* Coefficient */}
              <div className="w-32">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coefficient <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="coefficient"
                  value={formData.coefficient || 1}
                  onChange={handleChange}
                  step="0.5"
                  min="0.5"
                  max="10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
              </div>

              {/* Elements Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Éléments de module</h3>
                  <div className={`text-xs font-medium px-2 py-1 rounded ${
                    isWeightValid ? 'text-green-600 bg-green-50' : 'text-amber-600 bg-amber-50'
                  }`}>
                    Total: {totalWeight}% {!isWeightValid && '(doit être 100%)'}
                  </div>
                </div>

                {/* Elements Header */}
                <div className="grid grid-cols-[1fr_80px_100px_32px] gap-2 mb-2 text-xs text-gray-500 font-medium">
                  <span>Nom de l'élément</span>
                  <span className="text-center">Poids (%)</span>
                  <span className="text-center">Vol. horaire (h)</span>
                  <span></span>
                </div>

                {/* Elements List */}
                <div className="space-y-2">
                  {elements.map((element, idx) => (
                    <div key={element.id} className="grid grid-cols-[1fr_80px_100px_32px] gap-2 items-center">
                      <input
                        type="text"
                        value={element.name}
                        onChange={(e) => updateElement(element.id, 'name', e.target.value)}
                        placeholder={`Élément ${idx + 1}`}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <input
                        type="number"
                        value={element.weight}
                        onChange={(e) => updateElement(element.id, 'weight', parseFloat(e.target.value) || 0)}
                        min={0}
                        max={100}
                        className="w-full px-2 py-2 text-sm text-center border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <input
                        type="number"
                        value={element.volumeHoraire ?? ''}
                        onChange={(e) => updateElement(element.id, 'volumeHoraire', e.target.value ? parseFloat(e.target.value) : null)}
                        min={0}
                        step={0.5}
                        placeholder="Ex: 30"
                        className="w-full px-2 py-2 text-sm text-center border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeElement(element.id)}
                        disabled={elements.length <= 1}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add Element Button */}
                <button
                  type="button"
                  onClick={addElement}
                  className="mt-3 flex items-center gap-1 px-3 py-1.5 text-xs text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Ajouter un élément
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !isWeightValid}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Removed elements: show what their deletion wipes before saving */}
      <DeleteImpactModal
        isOpen={showDeleteImpact}
        targets={deletedElementIds.map((id) => ({ type: 'element', id }))}
        title={`Supprimer ${deletedElementIds.length > 1 ? `${deletedElementIds.length} éléments` : 'un élément'} ?`}
        itemName={module.name}
        message="Les éléments retirés seront supprimés à l'enregistrement, ainsi que les données rattachées ci-dessous."
        confirmLabel="Supprimer et enregistrer"
        isLoading={isSubmitting}
        onConfirm={() => { void performSave(); }}
        onCancel={() => setShowDeleteImpact(false)}
      />
    </div>
  );
};

export default EditModuleModal;
